import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useMutation } from "@apollo/react-hooks";
import { Box, Button } from "@mui/material";
import {
  MY_HR_SEND_TEMPORARY_PASSWORD,
  MY_HR_UPDATE_USER,
} from "my-hr/graphql";
import { useMyHrUserByEmployee } from "my-hr/hooks";
import {
  OcCard,
  OcCardButtonBar,
  OcEmailInputField,
  OcUsernameInputField,
  OcValidToInputField,
} from "oc/components";
import {
  useOcEmailInput,
  useOcIsMobile,
  useOcProperty,
  useOcSnackbar,
} from "oc/hooks";

export const MyHrUserProfileForm = () => {
  const { t, i18n } = useTranslation();
  const { setOpenMessage } = useOcSnackbar();
  const isMobile = useOcIsMobile();
  let { employee } = useParams<{ employee: string }>();
  const { data: userData } = useMyHrUserByEmployee({ employee });

  const { data: loginType } = useOcProperty({
    resource: "system",
    key: "loginType",
  });

  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const [updateUser] = useMutation(MY_HR_UPDATE_USER, {
    onError() {
      setLoading(false);
    },
    onCompleted() {
      setLoading(false);
      setOpenMessage({
        type: "success",
        message: t("OC_USER_UPDATED"),
      });
    },
  });

  const [sendTemporaryPassword] = useMutation(MY_HR_SEND_TEMPORARY_PASSWORD, {
    onError() {
      setLoading(false);
    },
    onCompleted() {
      setLoading(false);
      setOpenMessage({
        type: "success",
        message: t("MY_HR_TEMPORARY_PASSWORD_EMAIL_SENT"),
      });
    },
  });

  const {
    value: email,
    setValue: setEmail,
    isValid: isEmailValid,
    validate: validateEmail,
    error: emailError,
    setError: setEmailError,
    helperText: emailHelperText,
    setHelperText: setEmailHelperText,
  } = useOcEmailInput();

  const [username, setUsername] = useState("");
  const [usernameError, setUsernameError] = useState(false);
  const [usernameHelperText, setUsernameHelperText] = useState("");

  const [validTo, setValidTo] = useState<Date | null>(null);
  const [validToError, setValidToError] = useState(false);
  const [validToHelperText, setValidToHelperText] = useState("");

  useEffect(() => {
    if (userData && !initialized) {
      if (userData.email) setEmail(userData.email);
      if (userData.ldapUser) setUsername(userData.ldapUser);
      if (userData.validTo) setValidTo(userData.validTo);

      setInitialized(true);
    }
  }, [userData, initialized, setEmail]);

  const handleUpdate = () => {
    let valid = true;

    if (!isEmailValid()) {
      validateEmail();
      valid = false;
    }

    if (loginType === "sso" && !username) {
      setUsernameError(true);
      setUsernameHelperText(t("MY_HR_ERROR_MESSAGE_INVALID_USERNAME"));
      valid = false;
    }

    if (validTo && Number.isNaN(validTo.getTime())) {
      setValidToError(true);
      setValidToHelperText(t("OC_NOT_SELECTABLE"));
      valid = false;
    }

    if (!valid) return;

    const params: {
      _id: any;
      email: string;
      ldapUser?: string;
      validTo?: any;
    } = {
      _id: userData?._id,
      email: email,
    };

    if (loginType === "sso") {
      params.ldapUser = username;
    }

    if (userData?.validTo !== validTo) {
      params.validTo = validTo;
    }

    setLoading(true);

    updateUser({
      variables: params,
    });
    setValidToError(false);
    setValidToHelperText("");
  };

  const handleCancel = () => {
    setEmail(userData?.email || "");
    setUsername(userData?.ldapUser || "");
    setValidTo(userData?.validTo || null);
    setValidToError(false);
    setValidToHelperText("");
  };

  const handleSendTemporaryPassword = () => {
    let email = userData?.email;
    if (email) {
      setLoading(true);
      sendTemporaryPassword({
        variables: { email, locale: i18n.language },
      });
    }
  };

  let disabled = true;

  if (userData?.email !== email) {
    disabled = false;
  }

  if (loginType === "sso" && userData?.ldapUser !== username) {
    disabled = false;
  }

  if (
    (userData?.validTo &&
      validTo &&
      new Date(userData.validTo).toLocaleDateString() !==
      new Date(validTo).toLocaleDateString()) ||
    (!userData?.validTo && validTo) ||
    (userData?.validTo && !validTo)
  ) {
    disabled = false;
  }

  return (
    <React.Fragment>
      <OcCard label={t("MY_HR_USER_ACCOUNT")} loading={loading}>
        <>
          <Box id="my-hr-user-profile-data">
            <Box paddingTop="24px" width={isMobile ? "auto" : "432px"}>
              {loginType === "sso" && (
                <Box>
                  <OcUsernameInputField
                    value={username}
                    setValue={setUsername}
                    error={usernameError}
                    setError={setUsernameError}
                    helperText={usernameHelperText}
                    setHelperText={setUsernameHelperText}
                  />
                  <Box height="36px" />
                </Box>
              )}
              <OcEmailInputField
                value={email}
                setValue={setEmail}
                error={emailError}
                setError={setEmailError}
                helperText={emailHelperText}
                setHelperText={setEmailHelperText}
              />
            </Box>
            {loginType !== "sso" && (
              <Box paddingTop="36px">
                <Box
                  width={isMobile ? "auto" : "432px"}
                  borderBottom="1px solid #C6CACC"
                />
                <Box
                  fontSize="16px"
                  letterSpacing="0.44px"
                  color="#4D4D4D"
                  padding="36px 0px"
                >
                  {t("MY_HR_SEND_TEMPORARY_PASSWORD_TEXT")}
                </Box>
                <Box
                  width={isMobile ? "auto" : "432px"}
                  paddingBottom="36px"
                  borderBottom="1px solid #C6CACC"
                >
                  <Button
                    variant="contained"
                    color="primary"
                    disableElevation
                    fullWidth
                    onClick={handleSendTemporaryPassword}
                  >
                    {t("MY_HR_SEND_NEW_TEMPORARY_PASSWORD")}
                  </Button>
                </Box>
              </Box>
            )}
            <Box
              width={isMobile ? "auto" : "432px"}
              padding="36px 0px 48px 0px"
            >
              <OcValidToInputField
                value={validTo}
                setValue={setValidTo}
                error={validToError}
                setError={setValidToError}
                helperText={validToHelperText}
                setHelperText={setValidToHelperText}
              />
            </Box>
            <OcCardButtonBar
              handleRightButton={handleCancel}
              rightButtonText={t("OC_CANCEL")}
              rightButtonColor="secondary"
              rightButtonId="handleUpdate-cancel-button"
              handlePrimaryButton={handleUpdate}
              primaryButtonText={t("OC_SAVE")}
              primaryButtonDisabled={disabled}
              primaryButtonId="handleUpdate-save-button"
            />
          </Box>
        </>
      </OcCard>
    </React.Fragment>
  );
};

export default MyHrUserProfileForm;
